import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, interval, of } from 'rxjs';
import { catchError, exhaustMap, filter, take, tap } from 'rxjs/operators';
import { AccountService, AlertService } from 'src/app/services/auth';
import { HormigonDispatchFacade } from '../../despacho/hormigon-dispatch.facade';
import {
  QR_CLIENT_POLL_MS,
  QrClientFirmaStatus,
  buildSeguimientoAnonimoParams,
  buildSeguimientoQrLink,
  deriveQrClientFirmaStatus,
} from '../../despacho/firma-digital-page/qr-client-firma-status';
import { HormigonHistoryFacade } from '../hormigon-history.facade';
import { HormigonHistoryRow } from '../hormigon-history.types';

const QR_POLL_SECONDS = Math.ceil(QR_CLIENT_POLL_MS / 1000);

@Component({
  selector: 'app-hormigon-history-detail',
  templateUrl: './hormigon-history-detail.component.html',
  styleUrls: ['./hormigon-history-detail.component.css'],
  standalone: false,
})
export class HormigonHistoryDetailComponent implements OnDestroy {
  @Input() order: HormigonHistoryRow | null = null;
  @Input() isRejected: (order: HormigonHistoryRow) => boolean = () => false;
  @Input() isDelivered: (order: HormigonHistoryRow) => boolean = () => false;
  @Input() isPending: (order: HormigonHistoryRow) => boolean = () => false;
  @Output() updated = new EventEmitter<void>();
  @Output() signed = new EventEmitter<void>();

  showDetalle = false;
  showAccion = false;
  showQr = false;

  qrLink = '';
  qrStatus: QrClientFirmaStatus = deriveQrClientFirmaStatus({});
  qrPollSecondsLeft = QR_POLL_SECONDS;
  qrPollOffline = false;

  private qrPollSub: Subscription | null = null;

  constructor(
    private readonly historyFacade: HormigonHistoryFacade,
    private readonly dispatchFacade: HormigonDispatchFacade,
    private readonly accountService: AccountService,
    private readonly router: Router,
    private readonly alertService: AlertService,
  ) {}

  ngOnDestroy(): void {
    this.stopQrPolling();
  }

  openDetalle(): void {
    this.showDetalle = true;
  }

  closeDetalle(): void {
    this.showDetalle = false;
  }

  openAccion(): void {
    this.showDetalle = false;
    this.showAccion = true;
  }

  closeAccion(): void {
    this.showAccion = false;
  }

  isSigned(order: HormigonHistoryRow): boolean {
    return order.isSignature;
  }

  generateQr(order: HormigonHistoryRow | null = this.order): void {
    if (!order) return;
    this.historyFacade
      .regenerateQr(String(order.tkt), String(order.orderNumber))
      .subscribe({
        next: (data: any) => {
          if (data?.Estado === 'Insertado') {
            this.closeAccion();
            this.openQr(order);
            return;
          }
          this.alertService.error('Error al generar QR.');
        },
        error: () => this.alertService.error('Error al generar QR.'),
      });
  }

  closeQr(): void {
    this.showQr = false;
    this.stopQrPolling();
  }

  get qrPollHintText(): string {
    if (this.qrPollOffline) {
      return this.qrPollSecondsLeft > 0
        ? `Sin conexión. Se reintentará en ${this.qrPollSecondsLeft} s`
        : 'Sin conexión. Reintentando…';
    }
    return this.qrPollSecondsLeft > 0
      ? `Verificando el estado actual de la firma mediante QR. Próxima en ${this.qrPollSecondsLeft} s`
      : 'Verificando el estado actual de la firma mediante QR';
  }

  moveToCurrentDispatch(): void {
    if (!this.order) return;
    this.historyFacade
      .setPendingOrder(String(this.order.tkt), String(this.order.orderNumber))
      .subscribe({
        next: (data: any) => {
          if (data?.Estado === 'Insertado') {
            this.closeAccion();
            this.updated.emit();
            void this.router.navigate(['/otrosdespachos']);
          }
        },
      });
  }

  private openQr(order: HormigonHistoryRow): void {
    this.stopQrPolling();
    this.qrLink = buildSeguimientoQrLink(
      buildSeguimientoAnonimoParams(this.accountService.userValue, order.tkt)
    );
    this.qrStatus = deriveQrClientFirmaStatus({});
    this.qrPollOffline = false;
    this.showQr = true;
    this.startQrPolling(String(order.tkt));
  }

  private startQrPolling(tkt: string): void {
    this.qrPollSecondsLeft = QR_POLL_SECONDS;
    this.qrPollSub = interval(1000)
      .pipe(
        tap(() => (this.qrPollSecondsLeft = Math.max(0, this.qrPollSecondsLeft - 1))),
        filter(() => this.qrPollSecondsLeft === 0 && !document.hidden),
        exhaustMap(() =>
          this.dispatchFacade.getByTicket(tkt, true, true).pipe(
            take(1),
            catchError(() => of(null))
          )
        )
      )
      .subscribe((rows) => {
        this.qrPollSecondsLeft = QR_POLL_SECONDS;
        const next = rows?.[0];
        this.qrPollOffline = !rows;
        if (!next) return;
        this.qrStatus = deriveQrClientFirmaStatus(next);
        if (this.qrStatus.signed) {
          this.stopQrPolling();
          this.signed.emit();
        }
      });
  }

  private stopQrPolling(): void {
    this.qrPollSub?.unsubscribe();
    this.qrPollSub = null;
  }
}
